{/*
  * DspTestingPage.tsx
  * https://dash.applovin.com/documentation/mediation/auctions/dsps/testing
  * Documentation > MAX Demand Partners > DSPs > Testing
  */}
import React, { FunctionComponent } from 'react';
import { ImageCard, IndentWidthType } from '../../../common';
import { DocsPageModule, LabelFontFamily } from 'UI-Dash-Commons-UI';
import { DSP_IMAGES } from '../../MediationDocs/redux/constants';

type DspTestingPageProps = {
    linkTargetTopPosition?: number;
}

const DspTestingPage: FunctionComponent<DspTestingPageProps> = ( { linkTargetTopPosition } ) =>
{
    return (
        <>
            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Testing'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_1}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'After you finish '}/>
                    <DocsPageModule.DocsPageContentAnchor
                        text={'integrating'}
                        to={'/documentation/mediation/auctions/dsps/ortb-spec/introduction'}
                        newWindow={false}
                    />
                <DocsPageModule.DocsPageContentText
                    text={' AppLovin RTB, you can use the AppLovin DSP Platform app to QA your ads. '
                        + 'Make sure to test and confirm that all ad formats and creative types are rendering correctly on the test app before you go live on the AppLovin Exchange.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'The bundle ID / package name for the AppLovin test app is '}/>
                <DocsPageModule.DocsPageContentCode children={'com.applovin.apps.dspdemo'}/>
                <DocsPageModule.DocsPageContentText text={'. You can find the app at the following URLs:'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageUnorderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Android: '}/>
                    <DocsPageModule.DocsPageContentAnchor
                        text={'https://play.google.com/store/apps/details?id=com.applovin.apps.dspdemo'}
                        to={'https://play.google.com/store/apps/details?id=com.applovin.apps.dspdemo'}
                        newWindow={true}
                    />
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'iOS: '}/>
                    <DocsPageModule.DocsPageContentAnchor
                        text={'https://apps.apple.com/us/app/applovin-dsp-platform/id1590263203'}
                        to={'https://apps.apple.com/us/app/applovin-dsp-platform/id1590263203'}
                        newWindow={true}
                    />
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageUnorderedList>
            <DocsPageModule.DocsPageContent
                text={`The DSP Sample requires fully secure creatives. This includes all tracking pixels. `
                    + `If a URL is not secure (http rather than https) it may not be called.`}
            />

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Test App Functionalities'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <DocsPageModule.DocsPageContent
                text={'The AppLovin DSP Platform has two main testing functionalities:'}/>

            <DocsPageModule.DocsPageOrderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText
                        text={'Testing your Bidder: '}
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText text={'Use the '} />
                    <DocsPageModule.DocsPageContentText
                        text={'DSP Integrations'}
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText
                        text={' functionality to run an end-to-end test of your bidder on the AppLovin Exchange for each ad format that AppLovin supports.'} />
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText
                        text={'Testing your Bid Response: '}
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText text={'Use the '} />
                    <DocsPageModule.DocsPageContentText
                        text={'Ad Debugging'}
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText text={' feature under '} />
                    <DocsPageModule.DocsPageContentText
                        text={'Advanced'}
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText text={' to test a bid response without pushing it to your bidder.'} />
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageOrderedList>
            <ImageCard
                alt={DSP_IMAGES[ 0 ][ 'alt' ]}
                imageSrc={DSP_IMAGES[ 0 ][ 'imageSrc' ]}
            />

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Testing Your Bidder (e2e)'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'Use the '}/>
                <DocsPageModule.DocsPageContentText
                    text={'DSP Integrations'}
                    fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                <DocsPageModule.DocsPageContentText text={' section of the app to test your bidder on the AppLovin Exchange end-to-end.'}/>
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageOrderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent>
                        <DocsPageModule.DocsPageContentText text={'Install the AppLovin DSP Platform test app for '}/>
                        <DocsPageModule.DocsPageContentAnchor
                            text={'Android'}
                            to={'https://play.google.com/store/apps/details?id=com.applovin.apps.dspdemo'}
                            newWindow={true} />
                        <DocsPageModule.DocsPageContentText text={' or '}/>
                        <DocsPageModule.DocsPageContentAnchor
                            text={'iOS'}
                            to={'https://apps.apple.com/us/app/applovin-dsp-platform/id1590263203'}
                            newWindow={true} />
                        <DocsPageModule.DocsPageContentText text={'.'}/>
                    </DocsPageModule.DocsPageContent>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent>
                        <DocsPageModule.DocsPageContentText text={'The app asks for your DSP Key. Enter the DSP/SDK Key found in the '}/>
                        <DocsPageModule.DocsPageContentAnchor text={'Keys'} to={'/o/account#keys'} newWindow={true} />
                        <DocsPageModule.DocsPageContentText text={' page of your AppLovin dashboard.'}/>
                    </DocsPageModule.DocsPageContent>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent
                        text={'Ensure your bidder is targeting the AppLovin DSP Platform app bundle ID with the campaign you want to test end-to-end. '
                            + 'Verify that your bid response is valid JSON.'} />
                    <DocsPageModule.DocsPageContent>
                        <DocsPageModule.DocsPageContentText text={'The AppLovin DSP Platform bundle ID is '}/>
                        <DocsPageModule.DocsPageContentCode children={'com.applovin.apps.dspdemo'}/>
                        <DocsPageModule.DocsPageContentText text={' for Android and '}/>
                        <DocsPageModule.DocsPageContentCode children={'1590263203'}/>
                        <DocsPageModule.DocsPageContentText text={' for iOS.'}/>
                    </DocsPageModule.DocsPageContent>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent>
                        <DocsPageModule.DocsPageContentText text={'Open the app and identify the '}/>
                        <DocsPageModule.DocsPageContentText text={'DSP Integrations'} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                        <DocsPageModule.DocsPageContentText text={' section.'}/>
                    </DocsPageModule.DocsPageContent>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContent
                        text={'Select an ad format to validate that your bidder is able to successfully win the auction and render the ad.'}/>
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageOrderedList>

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Testing Your Bid Response and Creative'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'Test your bid response without pushing it to your bidder via the '}/>
                <DocsPageModule.DocsPageContentText
                    text={'Advanced > Ad Debugging'}
                    fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                <DocsPageModule.DocsPageContentText text={' section.'}/>
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageOrderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Install the AppLovin DSP Platform test app for '}/>
                    <DocsPageModule.DocsPageContentAnchor
                        text={'Android'}
                        to={'https://play.google.com/store/apps/details?id=com.applovin.apps.dspdemo'}
                        newWindow={true} />
                    <DocsPageModule.DocsPageContentText text={' or '}/>
                    <DocsPageModule.DocsPageContentAnchor
                        text={'iOS'}
                        to={'https://apps.apple.com/us/app/applovin-dsp-platform/id1590263203'}
                        newWindow={true} />
                    <DocsPageModule.DocsPageContentText text={'.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'The app asks for your DSP Key. Enter the DSP/SDK Key found in the '}/>
                    <DocsPageModule.DocsPageContentAnchor
                        text={'Keys'}
                        to={'/o/account#keys'}
                        newWindow={true} />
                    <DocsPageModule.DocsPageContentText text={' page of your AppLovin dashboard.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Select '}/>
                    <DocsPageModule.DocsPageContentText
                        text={'Advanced > Ad Debugging'}
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText text={'.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Paste your JSON bid response into the text field.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Select the ad format that corresponds to your bid response.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText
                        text={'If your bid response is valid, AppLovin render the creative on the screen. '
                            + 'Otherwise, AppLovin returns an error message.'}/>
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageOrderedList>

            <DocsPageModule.DocsPageContentSpace withSpace={IndentWidthType.SMALL} />
            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Launch'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_1}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'When you are ready to launch, AppLovin completes quality-assurance testing and then sets your bidder live with real traffic. '
                    + 'Make sure to turn off test ads and begin using real campaigns at this time. '
                    + 'Applovin begins by sending 1% of live traffic to your endpoint(s). '
                    + 'If AppLovin impression and revenue numbers are aligned, your AppLovin account team continues to increase traffic to your bidder, from 1% to 10% to 100%.'}/>
            </DocsPageModule.DocsPageContent>
        </>
    )
}

export default DspTestingPage;
